import { useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import {
  CALL_PROVIDERS,
  getJsonFromSecureLocalStorage,
} from "modules/utilities/utility";
import { usePlivoStore, CALL_STATES } from "./store";

/**
 * Global Plivo SDK listener — mounts at layout level.
 * Handles SDK initialization and incoming call UI (Accept/Reject).
 * Outbound call logic stays in PlivoIntegration.jsx on actionables pages.
 */
const PlivoGlobalListener = () => {
  const plivoClientRef = useRef(null);
  const ringtoneRef = useRef(null);

  const {
    callState,
    resetCall,
  } = usePlivoStore();

  const navigate = useNavigate();
  const incomingCallerInfo = usePlivoStore((s) => s.incomingCallerInfo);

  // Play/stop ringtone based on INCOMING state
  useEffect(() => {
    if (callState === CALL_STATES.INCOMING) {
      try {
        const audio = new Audio("https://cdn.pixabay.com/audio/2022/10/01/audio_4966d4e670.mp3");
        audio.loop = true;
        audio.play().catch(() => {});
        ringtoneRef.current = audio;
      } catch (e) {
        console.log("PLIVO_GLOBAL :: ringtone play failed", e);
      }
    } else {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current = null;
      }
    }
    return () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current = null;
      }
    };
  }, [callState]);

  // Initialize Plivo Browser SDK on mount
  useEffect(() => {
    const callProviders = getJsonFromSecureLocalStorage("call_providers") || [];
    const plivoProvider = callProviders.find(
      (p) => p.provider_name === CALL_PROVIDERS.PLIVO
    );

    const endpointUsername = _.get(plivoProvider, "endpoint_username", "");
    const endpointPassword = _.get(plivoProvider, "endpoint_password", "");

    if (!endpointUsername || !endpointPassword) return;

    // Prevent double initialization (React strict mode double-mounts refs reset)
    if (window.__plivoClient) {
      plivoClientRef.current = window.__plivoClient;
      console.log("PLIVO_GLOBAL :: reusing existing SDK client");
      return;
    }

    try {
      const PlivoSDK = require("plivo-browser-sdk");
      const plivoWrapper = new PlivoSDK({ debug: "ERROR", permOnClick: true, enableTracking: true });
      const client = plivoWrapper.client;

      // Disable built-in ringtone/ringback/connect tones
      client.setRingTone(false);
      client.setRingToneBack(false);
      client.setConnectTone(false);

      client.on("onLogin", () => {
        console.log("PLIVO_GLOBAL :: logged in");
        usePlivoStore.getState().setSdkReady(true);
      });

      client.on("onLoginFailed", (cause) => {
        console.error("PLIVO_GLOBAL :: login failed", cause);
        usePlivoStore.getState().setSdkReady(false);
      });

      client.on("onLogout", () => {
        console.log("PLIVO_GLOBAL :: logged out");
        usePlivoStore.getState().setSdkReady(false);
      });

      client.on("onIncomingCall", (callerID, extraHeaders, callInfo) => {
        console.log("PLIVO_GLOBAL :: onIncomingCall", callerID, callInfo);
        console.log("PLIVO_GLOBAL :: extraHeaders", JSON.stringify(extraHeaders));
        const state = usePlivoStore.getState();
        if (state.callState === CALL_STATES.RINGING || state.callState === CALL_STATES.TRANSFERRING) {
          // Outbound call or MPC re-add after transfer — auto-answer
          console.log("PLIVO_GLOBAL :: auto-answering (state=" + state.callState + ")");
          client.answer();
        } else if (state.callState === CALL_STATES.AVAILABLE) {
          // Incoming call — extract customer context from SIP headers
          const caseId = decodeURIComponent(_.get(extraHeaders, "X-Ph-Caseid", "") || "");
          const customerName = decodeURIComponent(_.get(extraHeaders, "X-Ph-Customername", "") || "");
          const customerPhone = decodeURIComponent(_.get(extraHeaders, "X-Ph-Customerphone", "") || "");
          console.log(`PLIVO_GLOBAL :: incoming call context caseId=${caseId} customerName=${customerName}`);
          state.setIncomingCall({
            callerID: customerName || callerID || "Unknown",
            callInfo,
            caseId,
            customerName,
            customerPhone,
          });
        }
      });

      client.on("onCallRemoteRinging", (callInfo) => {
        console.log("PLIVO_GLOBAL :: onCallRemoteRinging", callInfo);
      });

      client.on("onCallAnswered", (callInfo) => {
        console.log("PLIVO_GLOBAL :: onCallAnswered", callInfo);
        const store = usePlivoStore.getState();
        if (store.callState === CALL_STATES.TRANSFERRING) {
          // Agent re-joined MPC after transfer
          store.setCallState(CALL_STATES.IN_MPC);
        } else {
          store.setCallState(CALL_STATES.CONNECTED);
        }
        store.startTimer();
      });

      client.on("onCallTerminated", (hangupInfo, callInfo) => {
        console.log("PLIVO_GLOBAL :: onCallTerminated", hangupInfo, callInfo);
        const store = usePlivoStore.getState();
        // During transfer, the original call drops — stay in TRANSFERRING to auto-answer MPC re-add
        if (store.callState === CALL_STATES.TRANSFERRING) {
          console.log("PLIVO_GLOBAL :: call terminated during transfer, waiting for MPC re-add");
          return;
        }
        store.stopTimer();
        store.resetCall();
      });

      client.on("onCallFailed", (cause, callInfo) => {
        console.error("PLIVO_GLOBAL :: onCallFailed", cause, callInfo);
        usePlivoStore.getState().resetCall();
      });

      client.on("onMediaPermission", (event) => {
        console.log("PLIVO_GLOBAL :: onMediaPermission", event);
      });

      // Set global ref BEFORE login to prevent React strict mode double-init
      window.__plivoClient = client;
      plivoClientRef.current = client;

      client.login(endpointUsername, endpointPassword);
      console.log("PLIVO_GLOBAL :: initialized, logging in...");
    } catch (err) {
      console.error("PLIVO_GLOBAL :: failed to initialize", err);
    }

    return () => {
      // Don't logout/cleanup — SDK should persist for the entire session.
      // React strict mode double-mounts would otherwise destroy the active session.
    };
  }, []);

  const handleAcceptIncoming = useCallback(() => {
    const client = plivoClientRef.current || window.__plivoClient;
    if (client) {
      console.log("PLIVO_GLOBAL :: Accepting incoming call");
      client.answer();
    }
    // Navigate to customer's lead-details page if caseId available
    const info = usePlivoStore.getState().incomingCallerInfo;
    const caseId = info?.caseId;
    if (caseId) {
      navigate(`/lead-details-v2/${caseId}`);
    }
  }, [navigate]);

  const handleRejectIncoming = useCallback(() => {
    const client = plivoClientRef.current || window.__plivoClient;
    if (client) {
      console.log("PLIVO_GLOBAL :: Rejecting incoming call");
      client.reject();
    }
    resetCall();
  }, [resetCall]);

  // Only render the incoming call UI
  if (callState !== CALL_STATES.INCOMING) return null;

  return (
    <>
      {createPortal(
        <style>{`@keyframes pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.15); opacity: 0.7; } }`}</style>,
        document.head
      )}
      {createPortal(
        <div style={incomingOverlayStyle}>
          <div style={incomingCardStyle}>
            <div style={incomingPulseStyle} />
            <div style={{ fontSize: 14, color: "#9CA3AF", marginBottom: 4 }}>
              Incoming Transfer Call
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#F3F4F6", marginBottom: 4 }}>
              {incomingCallerInfo?.customerName || incomingCallerInfo?.callerID || "Unknown"}
            </div>
            {incomingCallerInfo?.customerPhone && (
              <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 4 }}>
                {incomingCallerInfo.customerPhone}
              </div>
            )}
            <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
              <button style={rejectBtnStyle} onClick={handleRejectIncoming}>
                Reject
              </button>
              <button style={acceptBtnStyle} onClick={handleAcceptIncoming}>
                Accept
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const incomingOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10002,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.6)",
};

const incomingCardStyle = {
  backgroundColor: "#1F2937",
  borderRadius: 16,
  padding: "32px 40px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
};

const incomingPulseStyle = {
  width: 48,
  height: 48,
  borderRadius: "50%",
  backgroundColor: "#10B981",
  marginBottom: 16,
  animation: "pulse 1.5s ease-in-out infinite",
};

const acceptBtnStyle = {
  border: "none",
  borderRadius: 8,
  padding: "10px 32px",
  backgroundColor: "#10B981",
  color: "#fff",
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};

const rejectBtnStyle = {
  border: "none",
  borderRadius: 8,
  padding: "10px 32px",
  backgroundColor: "#EF4444",
  color: "#fff",
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};

export default PlivoGlobalListener;
